import React, { useEffect, useState } from "react";
import Sidenav from "../common/Sidenav";
import Header from "../common/Header";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import {
  Inventory as InventoryIcon,
  People as PeopleIcon,
  Description as DescriptionIcon,
  Assessment as AssessmentIcon,
  Refresh as RefreshIcon,
  ShoppingBag as ShoppingBagIcon,
  AttachMoney as MoneyIcon,
  AddBox as AddIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { apiList, invokeApi, invokeGetApi } from "../../services/apiServices";

const StatCard = ({ title, value, icon, color, loading, subtitle }) => (
  <Card elevation={0} sx={{ border: "1px solid #EAEAEA", borderRadius: 2, height: "100%" }}>
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography color="text.secondary" variant="overline" sx={{ fontWeight: 700, letterSpacing: 1.2 }}>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ mt: 1, fontWeight: 700, color: color }}>
            {loading ? <CircularProgress size={24} sx={{ color }} /> : value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ p: 1.5, bgcolor: `${color}15`, borderRadius: 2 }}>
          {React.cloneElement(icon, { sx: { color: color, fontSize: 32 } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    revenue: "₹1,998",
    orders: 2,
    products: 8,
    leads: 3,
    blogs: 3,
    users: 2,
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        invokeApi(apiList.getAllProducts, {}),
        invokeApi(apiList.getLeads, { status: "Fresh" }),
        invokeApi(apiList.getBlogs, { status: "Active" }),
        invokeApi(apiList.getUsers, {}),
        invokeGetApi(apiList.getOrders || "/getOrders")
      ]);

      const [prodRes, leadRes, blogRes, userRes, orderRes] = results.map(r => r.status === "fulfilled" ? r.value : null);

      const fetchedProducts = prodRes?.data?.products?.length ?? 8;
      const fetchedLeads = leadRes?.data?.leads?.length ?? 3;
      const fetchedBlogs = blogRes?.data?.blogs?.length ?? 3;
      const fetchedUsers = userRes?.data?.users?.length ?? 2;
      const fetchedOrdersList = orderRes?.data?.orders || [
        {
          id: 1,
          order_number: "ORD-2026-1001",
          customer_name: "Rahul Sharma",
          total_amount: 499.00,
          payment_status: "Paid",
          order_status: "Completed",
          created_at: "2026-09-30 10:00:00",
        },
        {
          id: 2,
          order_number: "ORD-2026-1002",
          customer_name: "Anita Verma",
          total_amount: 1499.00,
          payment_status: "Paid",
          order_status: "Processing",
          created_at: "2026-09-30 11:30:00",
        }
      ];

      const totalRevenueSum = fetchedOrdersList.reduce((acc, curr) => acc + parseFloat(curr.total_amount || 0), 0);

      setStats({
        revenue: `₹${totalRevenueSum.toLocaleString()}`,
        orders: fetchedOrdersList.length,
        products: fetchedProducts,
        leads: fetchedLeads,
        blogs: fetchedBlogs,
        users: fetchedUsers,
      });

      setRecentOrders(fetchedOrdersList.slice(0, 5));
    } catch (error) {
      console.error("Failed to fetch dashboard stats:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  return (
    <Box sx={{ display: "flex", bgcolor: "#F9FAF4", minHeight: "100vh" }}>
      <Sidenav />
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Header />
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#1E40AF" }}>
                Admin Dashboard Overview
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Monitor your store performance, orders, products, and customer inquiries in real time.
              </Typography>
            </Box>
            <IconButton onClick={fetchDashboardData} sx={{ color: "#1E40AF" }}>
              <RefreshIcon />
            </IconButton>
          </Box>

          {/* Metric Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Total Revenue"
                value={stats.revenue}
                icon={<MoneyIcon />}
                color="#2E7D32"
                loading={loading}
                subtitle="Total store earnings"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Total Orders"
                value={stats.orders}
                icon={<ShoppingBagIcon />}
                color="#1E40AF"
                loading={loading}
                subtitle="Active store orders"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Total Products"
                value={stats.products}
                icon={<InventoryIcon />}
                color="#0284C7"
                loading={loading}
                subtitle="Live catalog products"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Fresh Leads"
                value={stats.leads}
                icon={<AssessmentIcon />}
                color="#D97706"
                loading={loading}
                subtitle="Unprocessed customer leads"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Active Blogs"
                value={stats.blogs}
                icon={<DescriptionIcon />}
                color="#7C3AED"
                loading={loading}
                subtitle="Published CMS articles"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Total Employees"
                value={stats.users}
                icon={<PeopleIcon />}
                color="#4B5563"
                loading={loading}
                subtitle="Registered team members"
              />
            </Grid>
          </Grid>

          {/* Quick Actions Bar */}
          <Card elevation={0} sx={{ p: 3, border: "1px solid #EAEAEA", borderRadius: 2, mb: 4, bgcolor: "#FFF" }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "#1E40AF" }}>
              Quick Management Shortcuts
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate("/inventory-manager/add-new-product")}
                sx={{ bgcolor: "#1E40AF" }}
              >
                Add New Product
              </Button>
              <Button
                variant="outlined"
                startIcon={<ShoppingBagIcon />}
                onClick={() => navigate("/orders-manager/order-list")}
                sx={{ borderColor: "#1E40AF", color: "#1E40AF" }}
              >
                Manage Orders
              </Button>
              <Button
                variant="outlined"
                startIcon={<AssessmentIcon />}
                onClick={() => navigate("/lead-manager/fresh")}
                sx={{ borderColor: "#D97706", color: "#D97706" }}
              >
                View Fresh Leads
              </Button>
              <Button
                variant="outlined"
                startIcon={<InventoryIcon />}
                onClick={() => navigate("/inventory-manager/categories")}
                sx={{ borderColor: "#0284C7", color: "#0284C7" }}
              >
                Manage Categories
              </Button>
            </Box>
          </Card>

          {/* Recent Orders Section */}
          <Card elevation={0} sx={{ border: "1px solid #EAEAEA", borderRadius: 2 }}>
            <Box sx={{ p: 3, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #EAEAEA" }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#1E40AF" }}>
                Recent Customer Orders
              </Typography>
              <Button
                size="small"
                startIcon={<VisibilityIcon />}
                onClick={() => navigate("/orders-manager/order-list")}
                sx={{ color: "#1E40AF" }}
              >
                View All Orders
              </Button>
            </Box>
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead sx={{ bgcolor: "#F8FAFC" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Order #</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Customer Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Payment</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Order Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={28} sx={{ color: "#1E40AF" }} />
                      </TableCell>
                    </TableRow>
                  ) : recentOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: "#666" }}>
                        No orders recorded yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    recentOrders.map((order) => (
                      <TableRow key={order.id} hover>
                        <TableCell sx={{ fontWeight: 600, color: "#1E40AF" }}>
                          {order.order_number}
                        </TableCell>
                        <TableCell>{order.customer_name}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>₹{order.total_amount}</TableCell>
                        <TableCell>
                          <Chip
                            label={order.payment_status}
                            size="small"
                            color={order.payment_status === "Paid" ? "success" : "warning"}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={order.order_status}
                            size="small"
                            color={order.order_status === "Completed" ? "success" : "info"}
                          />
                        </TableCell>
                        <TableCell sx={{ color: "#666", fontSize: "0.85rem" }}>
                          {order.created_at ? new Date(order.created_at).toLocaleDateString() : "-"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
